// The branch graph (spec/dashboard.md section 12.1): lanes and edges computed from the commit logs of the game's branches —
// one lane per branch in the order given (the current branch first), a commit on the lane of the first branch whose log
// holds it, an edge from a commit to each parent that is in the picture (a diagonal one when the lanes differ).
import type { Commit } from '../api/types';

export interface GraphRow {
  sha: string;
  commit: Commit;
  /** the lane index (the branch the commit is drawn on) */
  lane: number;
  /** the branches whose head this commit is */
  heads: string[];
  /** the row indices of the parents that are in the picture, with their lanes */
  parents: { row: number; lane: number }[];
  /** a commit with two parents */
  merge: boolean;
}

export interface BranchLog {
  branch: string;
  head: string | null;
  commits: Commit[];
}

export interface Graph {
  rows: GraphRow[];
  lanes: string[];
}

const timeOf = (c: Commit) => (c.authoredAt ? Date.parse(c.authoredAt) : 0);

/**
 * The rows newest first across every log given. A commit's lane is the branch whose **first-parent chain** from its head
 * reaches it first (the branches in the order given, the current one first): a merged side branch keeps its own lane on
 * the target too, so the merge commit joins two lanes; a commit no chain reaches falls back to the first log that lists it.
 */
export function buildGraph(logs: BranchLog[]): Graph {
  const lanes = logs.map((l) => l.branch);
  const commits = new Map<string, Commit>();
  const fallback = new Map<string, number>();
  for (let lane = 0; lane < logs.length; lane++) {
    for (const c of logs[lane]!.commits) {
      if (!commits.has(c.sha)) commits.set(c.sha, c);
      if (!fallback.has(c.sha)) fallback.set(c.sha, lane);
    }
  }
  const laneOf = new Map<string, number>();
  for (let lane = 0; lane < logs.length; lane++) {
    let sha: string | undefined = logs[lane]!.head ?? logs[lane]!.commits[0]?.sha;
    while (sha && !laneOf.has(sha)) {
      laneOf.set(sha, lane);
      sha = commits.get(sha)?.parents[0];
    }
  }
  const seen = new Map<string, { commit: Commit; lane: number }>();
  for (const [sha, commit] of commits) seen.set(sha, { commit, lane: laneOf.get(sha) ?? fallback.get(sha) ?? 0 });
  const ordered = Array.from(seen.entries())
    .map(([sha, v]) => ({ sha, ...v }))
    .sort((a, b) => timeOf(b.commit) - timeOf(a.commit) || a.sha.localeCompare(b.sha));
  // parents must come after their children: a commit authored "later" than its child (clock skew) is moved below it
  const index = new Map(ordered.map((r, i) => [r.sha, i]));
  const rowsIn = topologicalOrder(ordered, index);
  const rowIndex = new Map(rowsIn.map((r, i) => [r.sha, i]));
  const heads = new Map<string, string[]>();
  for (const l of logs) if (l.head) heads.set(l.head, [...(heads.get(l.head) ?? []), l.branch]);
  const rows: GraphRow[] = rowsIn.map((r) => ({
    sha: r.sha,
    commit: r.commit,
    lane: r.lane,
    heads: heads.get(r.sha) ?? [],
    parents: r.commit.parents.filter((p) => rowIndex.has(p)).map((p) => ({ row: rowIndex.get(p)!, lane: rowsIn[rowIndex.get(p)!]!.lane })),
    merge: r.commit.parents.length > 1,
  }));
  return { rows, lanes };
}

/** Keeps the time order except where a parent would precede its child. */
function topologicalOrder<T extends { sha: string; commit: Commit }>(ordered: T[], index: Map<string, number>): T[] {
  const out: T[] = [];
  const placed = new Set<string>();
  const byChildren = new Map<string, number>(); // sha → children still unplaced
  for (const r of ordered) for (const p of r.commit.parents) if (index.has(p)) byChildren.set(p, (byChildren.get(p) ?? 0) + 1);
  const pending = [...ordered];
  while (pending.length > 0) {
    const i = pending.findIndex((r) => (byChildren.get(r.sha) ?? 0) === 0);
    const r = pending.splice(i < 0 ? 0 : i, 1)[0]!;
    out.push(r);
    placed.add(r.sha);
    for (const p of r.commit.parents) if (byChildren.has(p)) byChildren.set(p, Math.max(0, (byChildren.get(p) ?? 1) - 1));
  }
  return out;
}

/** The lanes a row's edges pass through below it — for the renderer to draw the vertical lines of the lanes still alive. */
export function activeLanesBelow(rows: GraphRow[], row: number): Set<number> {
  const active = new Set<number>();
  for (let i = 0; i <= row; i++) for (const p of rows[i]!.parents) if (p.row > row) active.add(p.lane);
  return active;
}
